import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Brain, Loader2, AlertCircle, MessageSquare } from 'lucide-react';
import useAuth from '@/hooks/stores/useAuth';
import { formService } from '@/services/formService';
import { toast } from 'react-hot-toast';

const TranscriptionDashboard = () => {
  const navigate = useNavigate();
  const userId = useAuth((state) => state.userId);
  const [activeTab, setActiveTab] = useState('transcription');
  const [transcriptionData, setTranscriptionData] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasFetched = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      // Prevent multiple API calls
      if (!userId || hasFetched.current) {
        if (!userId) {
          setError('User not authenticated');
          setIsLoading(false);
        }
        return;
      }

      hasFetched.current = true;

      try {
        setIsLoading(true);
        
        // Fetch transcription data
        const transcriptionResponse = await formService.getStreamTranscription(userId);
        console.log('Stream Transcription API Response:', transcriptionResponse);
        setTranscriptionData(transcriptionResponse);

        // Fetch analysis data
        const analysisResponse = await formService.getUserForm(userId);
        if (analysisResponse?.transcription) {
          const parsedTranscription = typeof analysisResponse.transcription === 'string' 
            ? JSON.parse(analysisResponse.transcription) 
            : analysisResponse.transcription;
          setAnalysisData(parsedTranscription);
        }

      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load data');
        toast.error('Failed to load transcription data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userId]);

  const handleBack = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-sm font-normal text-muted-foreground">Loading transcription data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center justify-center py-20">
            <div className="text-center max-w-md mx-auto px-4">
              <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
              <h2 className="text-base font-semibold text-foreground mb-2">Error Loading Data</h2>
              <p className="text-sm font-normal text-muted-foreground mb-6">{error}</p>
              <button
                onClick={handleBack}
                className="w-full py-3 bg-primary text-primary-foreground rounded-[4px] hover:bg-primary/90 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="flex items-center mb-8">
          <button
            onClick={handleBack}
            className="flex items-center text-muted-foreground hover:text-foreground transition-colors mr-4"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            Back
          </button>
          <div className="flex items-center">
            <FileText className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-2xl font-semibold text-foreground">Transcription Dashboard</h1>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <div className="flex space-x-1 bg-secondary border border-secondary-border rounded-lg p-1">
            <button
              onClick={() => setActiveTab('transcription')}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'transcription'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
              }`}
            >
              <FileText className="h-4 w-4 mr-2" />
              View Transcription
            </button>
            
            <button
              onClick={() => setActiveTab('analysis')}
              className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'analysis'
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
              }`}
            >
              <Brain className="h-4 w-4 mr-2" />
              View Analysis
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'transcription' && (
          <div className="bg-secondary border border-secondary-border rounded-xl p-8">
            <h3 className="text-base font-semibold text-foreground mb-6">
              Conversation Transcription
            </h3>
            
            {transcriptionData ? (
              <div className="space-y-3">
                {transcriptionData.split('\n').map((line, index) => {
                  if (!line.trim()) return null;
                  
                  const isSpeaker0 = line.startsWith('Speaker 0:');
                  const isSpeaker1 = line.startsWith('Speaker 1:');
                  
                  if (isSpeaker0 || isSpeaker1) {
                    const speaker = isSpeaker0 ? 'Speaker 0' : 'Speaker 1';
                    const text = line.replace(/^Speaker [01]:\s*/, '');
                    
                    return (
                      <div 
                        key={index} 
                        className={`flex ${isSpeaker1 ? 'justify-end' : 'justify-start'}`}
                      >
                        <div 
                          className={`max-w-[80%] rounded-lg p-4 border border-secondary-border ${
                            isSpeaker0 
                              ? 'bg-background' 
                              : 'bg-primary/10'
                          }`}
                        >
                          <div className="flex items-center mb-1">
                            <span 
                              className={`text-sm font-semibold ${
                                isSpeaker0 ? 'text-muted-foreground' : 'text-primary'
                              }`}
                            >
                              {speaker}
                            </span>
                          </div>
                          <p className="text-sm font-normal text-foreground">{text}</p>
                        </div>
                      </div>
                    );
                  }
                  
                  return null;
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-base font-semibold text-foreground mb-2">No Transcription Available</h2>
                <p className="text-sm font-normal text-muted-foreground">
                  No transcription data was found for your session.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'analysis' && (
          <div className="bg-secondary border border-secondary-border rounded-xl p-8">
            <h3 className="text-base font-semibold text-foreground mb-6">
              Transcription Analysis
            </h3>
            
            {analysisData && analysisData.length > 0 ? (
              <div className="space-y-6">
                {analysisData.map((topic, topicIndex) => (
                  <div key={topicIndex} className="bg-background rounded-lg p-6 border border-secondary-border">
                    <h2 className="text-base font-semibold text-foreground mb-4 pb-2 border-b border-secondary-border flex items-center">
                      <Brain className="w-5 h-5 text-primary mr-2" />
                      {topic.topic_name}
                    </h2>
                    
                    <div className="space-y-6">
                      {topic.qa_pairs?.map((qa, qaIndex) => (
                        <div key={qaIndex} className="border border-secondary-border rounded-lg p-4 bg-secondary">
                          {/* Question */}
                          <div className="mb-4">
                            <div className="flex items-center mb-2">
                              <MessageSquare className="w-4 h-4 text-primary mr-2" />
                              <h3 className="text-base font-semibold text-foreground">Question</h3>
                            </div>
                            <p className="text-sm font-normal text-muted-foreground bg-background rounded p-3">
                              {qa.question}
                            </p>
                          </div>

                          {/* Answer */}
                          <div className="mb-4">
                            <div className="flex items-center mb-2">
                              <FileText className="w-4 h-4 text-primary mr-2" />
                              <h3 className="text-base font-semibold text-foreground">Answer</h3>
                            </div>
                            <p className="text-sm font-normal text-muted-foreground bg-background rounded p-3">
                              {qa.answer}
                            </p>
                          </div>

                          {/* Analysis Reason */}
                          {qa.reason && (
                            <div>
                              <div className="flex items-center mb-2">
                                <Brain className="w-4 h-4 text-primary mr-2" />
                                <h3 className="text-base font-semibold text-foreground">Analysis</h3>
                              </div>
                              <p className="text-sm font-normal text-muted-foreground bg-primary/5 rounded p-3 border border-secondary-border">
                                {qa.reason}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Brain className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-base font-semibold text-foreground mb-2">No Analysis Available</h2>
                <p className="text-sm font-normal text-muted-foreground">
                  No analysis data was found for your session.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TranscriptionDashboard;